import React from 'react';
import styled from 'styled-components';
import {Button} from 'epic-ue-components';
import {ButtonProps} from 'epic-ue-shared';
import {StyledTypography} from '../Sdk.styles';
import {HomeSectionCardLink} from '../../home/components/SectionCard';
import ReleaseNotesSection, {ReleaseNotesSectionProps} from './ReleaseNotesSection';

export interface MainSectionProps {
    title: string;
    description: string;
    showReleaseNotesSection?: boolean;
    sdkDownloadSection: {
        title: string;
        description1: string;
        description2: string;
        inputPlaceholder: string;
        underInputLink: HomeSectionCardLink;
        button: ButtonProps;
        media: {
            image: string;
            alt: string;
        };
    };
    infoSections: {
        title: string;
        description: string;
        button: ButtonProps;
        media: {
            image: string;
            alt: string;
        };
    }[];
    releaseNotesSection?: ReleaseNotesSectionProps;
    render: ({inputLabel, buttonLabel}: {inputLabel: string; buttonLabel?: string}) => void;
}

const Main = styled.main`
    display: flex;
    align-items: center;
    flex-direction: column;
    padding: 0 100px;
    margin-bottom: 100px;

    ${p => p.theme.breakpoints.down('lg')} {
        padding: 0 50px;
    }
    ${p => p.theme.breakpoints.down('md')} {
        padding: 0 24px;
    }
`;

const TextBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const MainTextBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 90px;
    max-width: 752px;
    padding: 16px;
`;

const VerticalSection = styled.div<{even?: boolean}>`
    width: 100%;
    max-width: 1248px;
    display: grid;
    gap: 32px;
    grid-template-areas:    'info'
                            'image';

    &:not(:last-of-type) {
        margin-bottom: 48px;
    }

    ${p => p.theme.breakpoints.up('md')} {
        grid-template-columns: 1fr 1fr;
        gap: 60px;
        align-items: center;
        grid-template-areas: "${p => (p.even ? 'info image' : 'image info')}";

        &:not(:last-of-type) {
            margin-bottom: 80px;
        }
    }
`;

const CircleDiv = styled.div`
    width: 59px;
    height: 59px;
    border-radius: 50%;
    border: 3px solid #0074e4;
    display: flex;
    align-items: center;
    justify-content: center;

    color: #f1f1f1;
    font-size: 30px;
    font-weight: 700;
    line-height: 36px;
    font-family: 'Inter';
`;

const InfoBlock = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
    justify-content: center;
    padding: 5px;
    grid-area: info;

    ${p => p.theme.breakpoints.down('sm')} {
        align-items: center;
    }
`;

const StyledImage = styled.img`
    width: 100%;
    heigth: 100%;
    grid-area: image;
`;

const StyledLink = styled.a`
    color: #338de6;
    font-family: 'Inter';
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
    width: max-content;

    &:hover {
        color: #58a9ff;
        text-decoration: none;
    }
`;

const MainSection: React.FC<MainSectionProps> = props => {
    if (!props) return null;

    const isReleaseNotesShown = props.showReleaseNotesSection;

    return (
        <Main>
            <MainTextBlock>
                <StyledTypography fontSize={36} fontWeight={400} lineHeight={44} textAlign="center" letterSpacing={-0.2}>
                    {props.title}
                </StyledTypography>
                <StyledTypography fontSize={18} fontWeight={400} lineHeight={22} textAlign="center">
                    {props.description}
                </StyledTypography>
            </MainTextBlock>

            <VerticalSection even>
                <InfoBlock>
                    <CircleDiv>{1}</CircleDiv>
                    <TextBlock>
                        <StyledTypography customColor="#fff" fontSize={30} fontWeight={400} lineHeight={36} letterSpacing={-0.3}>
                            {props.sdkDownloadSection.title}
                        </StyledTypography>
                        <StyledTypography customColor="rgba(255, 255, 255, 0.7)">{props.sdkDownloadSection.description1}</StyledTypography>
                    </TextBlock>
                    {props.render({inputLabel: props.sdkDownloadSection.inputPlaceholder, buttonLabel: props.sdkDownloadSection.button.label})}
                    <TextBlock>
                        <StyledLink href={props.sdkDownloadSection.underInputLink.href} target="_blank">
                            {props.sdkDownloadSection.underInputLink.title}
                        </StyledLink>
                        {/* <StyledLink href={`#${RELEASE_NOTES_SECTION_ID}`}>{props.sdkDownloadSection.underInputLink.title}</StyledLink> */}
                        <StyledTypography customColor="rgba(255, 255, 255, 0.7)">{props.sdkDownloadSection.description2}</StyledTypography>
                    </TextBlock>
                </InfoBlock>
                <StyledImage src={props.sdkDownloadSection.media?.image} alt={props.sdkDownloadSection.media?.alt} loading="lazy" />
            </VerticalSection>

            {/** Considered that first section will be the Download SKD one so the following sections' numeration will start from 2 */
            props.infoSections.map((section, i) => (
                <VerticalSection key={i} even={Boolean(i % 2)}>
                    <InfoBlock>
                        <CircleDiv>{i + 2}</CircleDiv>
                        <TextBlock>
                            <StyledTypography customColor="#fff" fontSize={30} fontWeight={400} lineHeight={36} letterSpacing={-0.3}>
                                {section.title}
                            </StyledTypography>
                            <StyledTypography customColor="rgba(255, 255, 255, 0.7)">{section.description}</StyledTypography>
                        </TextBlock>
                        <Button href={section.button.href} target="_blank">
                            {section.button.label}
                        </Button>
                    </InfoBlock>
                    <StyledImage src={section.media?.image} alt={section.media?.alt} loading="lazy" />
                </VerticalSection>
            ))}

            {isReleaseNotesShown && <ReleaseNotesSection {...props.releaseNotesSection} />}
        </Main>
    );
};

export default MainSection;
